import { useTranslation } from "react-i18next";
import { Calendar, MapPin, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { absoluteUrl, SITE_NAME, SITE_DESCRIPTION } from "@/seo/site";
import { organizationLd, websiteLd, sportsEventLd } from "@/seo/structuredData";
import { HeroPosterFaithful } from "@/pages/home/HeroPosterFaithful";
import { CountdownSection } from "@/pages/home/CountdownSection";
import { StoryIntro } from "@/pages/home/StoryIntro";
import { LaunchVideo } from "@/pages/home/LaunchVideo";

/**
 * Home page orchestrator. The hero mirrors the 2026 key visual
 * (poster-faithful), immediately followed by the When/Where/Time essentials
 * band; the narrative beneath follows StoryIntro → LaunchVideo — then hands off
 * to the shared `SponsorBanner` rendered by `PublicLayout` above the footer.
 */
export default function HomePage() {
  useDocumentMeta({
    title: `${SITE_NAME} | KL Corporate Relay Race`,
    description: SITE_DESCRIPTION,
    canonicalUrl: absoluteUrl("/"),
    jsonLd: [organizationLd(), websiteLd(), sportsEventLd()],
  });

  return (
    <>
      <HeroPosterFaithful />
      <KeyDetails />

      {/* Permanent countdown to flag-off (28 Nov 2026). */}
      <CountdownSection />

      <StoryIntro />
      <LaunchVideo />
    </>
  );
}

/**
 * Three-card band of event essentials — When, Where, Time — sitting
 * directly beneath the hero key visual at the top of the home page.
 */
function KeyDetails() {
  const { t } = useTranslation("home");
  // Celebration accent per card — mirrors the hero FactChips and the
  // WhyPillars numerals so the multi-colour runner palette carries through.
  // Tailwind needs static class names, so the tone classes are spelled out.
  const items = [
    {
      icon: Calendar,
      label: t("keyDetails.when.label"),
      value: t("keyDetails.when.value"),
      iconClass: "bg-accent-teal/10 text-accent-teal"
    },
    {
      icon: MapPin,
      label: t("keyDetails.where.label"),
      value: t("keyDetails.where.value"),
      iconClass: "bg-accent-orange/10 text-accent-orange"
    },
    {
      icon: Clock,
      label: t("keyDetails.time.label"),
      value: t("keyDetails.time.value"),
      iconClass: "bg-accent-magenta/10 text-accent-magenta"
    }
  ];
  return (
    <section className="bg-muted/30 border-y border-border">
      <div className="container py-16">
        <ScrollReveal>
          <div className="grid gap-6 mx-auto w-fit sm:w-full sm:mx-0 sm:grid-cols-3">
            {items.map((item) => (
              <Card key={item.label} className="border-none shadow-none bg-transparent">
                <CardContent className="p-0 flex items-start gap-4">
                  <div className={`rounded-lg p-3 ${item.iconClass}`}>
                    <item.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{item.label}</p>
                    <p className="mt-1 font-display text-2xl tracking-normal">{item.value}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
